/**
 * Geodetic types + a local-tangent-plane projection.
 *
 * The flight model and navigation run in a flat local NED frame (metres); this
 * module is the bridge that lets world features (airports, runways, navaids) be
 * authored at real lat/lon and projected into that frame. A single datum anchors
 * the whole plane, so every projected feature shares one consistent local origin.
 */

/** A point on the earth: latitude/longitude in degrees, elevation in metres MSL. */
export interface GeoPoint {
  lat: number;
  lon: number;
  elevation: number;
}

/** A horizontal point in the local NED plane (metres). */
export interface LocalNE {
  n: number;
  e: number;
}

// Mean metres per degree of latitude. Good to ~0.5% everywhere; plenty for a
// light-GA trainer operating within tens of NM of one airport.
const M_PER_DEG_LAT = 111320;

/**
 * Equirectangular (ENU tangent-plane) projection anchored at a datum.
 *
 * The east/west metres-per-degree scale is computed ONCE from the datum
 * latitude and held constant across the whole plane. That's the usual
 * small-area simplification, and it has a useful property here: because the
 * scale factors are constants, `geoToLocal` and `localToGeo` are EXACT
 * inverses. So a runway authored geographically from `localToGeo(someNE)`
 * re-derives `someNE` to floating-point precision — which is how the legacy
 * single-runway world keeps its exact NED coordinates after being restated in
 * lat/lon.
 *
 * Limitation: cross-track distortion grows with distance from the datum (the
 * longitude scale is frozen at the datum latitude, and the flat-earth
 * assumption is baked into the rest of the sim too). Fine near the airport;
 * for long cross-country flight the intended future hook is to RE-ANCHOR —
 * construct a new `LocalProjection` at a nearer datum and translate the
 * aircraft's NED state by the delta between the two origins. Not implemented
 * yet; there is only one datum today.
 */
export class LocalProjection {
  private readonly mPerDegLon: number;

  constructor(readonly datum: GeoPoint) {
    this.mPerDegLon = M_PER_DEG_LAT * Math.cos((datum.lat * Math.PI) / 180);
  }

  /** Project a geographic point to local NED horizontal metres. */
  geoToLocal(p: GeoPoint): LocalNE {
    return {
      n: (p.lat - this.datum.lat) * M_PER_DEG_LAT,
      e: (p.lon - this.datum.lon) * this.mPerDegLon,
    };
  }

  /** Inverse of geoToLocal: local NED metres (+ MSL elevation) back to lat/lon. */
  localToGeo(ne: LocalNE, elevation = 0): GeoPoint {
    return {
      lat: this.datum.lat + ne.n / M_PER_DEG_LAT,
      lon: this.datum.lon + ne.e / this.mPerDegLon,
      elevation,
    };
  }
}
